/**
 * Book Appointment Modal (MVP)
 *
 * Locations: Sesami storefront config (source of truth)
 *
 * Open via:
 *   - click on [data-book-appointment-trigger]
 *   - window.dispatchEvent(new CustomEvent('book-appointment:open', { detail: {...} }))
 *
 * Event / data attributes:
 *   sesamiLocationId | data-sesami-location-id  (preferred location preselect)
 *   location         | data-location            (Sesami id, or location name / slug)
 *   productId        | data-product-id          (Sesami service product id)
 *   variantId        | data-variant-id
 *   productType      | data-product-type        (match service by type/title, e.g. Engagement Rings)
 *   wishlist         | data-wishlist-url        (Swym share link, preferred)
 *                    | data-wishlist-items      (plain-text recap, guest fallback)
 *                    | (logged-in customers with no trigger value get their Swym
 *                    |  share link fetched on open — see _fetchWishlistShareUrl)
 *   relatedProduct   | data-related-product     (link back to the product the booking started
 *                    |                          from; defaults to the current URL on product pages)
 */

const STEPS = ['location', 'service', 'datetime', 'review', 'confirmation'];
const OPEN_EVENT = 'book-appointment:open';
const CLOSE_EVENT = 'book-appointment:close';

// Shared with wishlist-page.js so both reuse the same Swym share link.
const SS_WISHLIST_SHARE_KEY = 'spence-wishlist-share-url';
// Cap how long submit waits on Swym before booking without the wishlist.
const WISHLIST_SUBMIT_TIMEOUT_MS = 4000;

const TIMEZONES = [
  { id: 'America/Vancouver', label: 'Pacific Time' },
  { id: 'America/Edmonton', label: 'Mountain Time' },
  { id: 'America/Winnipeg', label: 'Central Time' },
  { id: 'America/Toronto', label: 'Eastern Time' },
  { id: 'America/Halifax', label: 'Atlantic Time' },
];

// Sesami's location payload has no timezone field, so derive it from the
// resolved province. Without this the modal falls back to the visitor's own
// timezone and books the appointment at the wrong wall-clock time.
const PROVINCE_TIMEZONES = {
  ALBERTA: 'America/Edmonton',
  BC: 'America/Vancouver',
  ONTARIO: 'America/Toronto',
  MANITOBA: 'America/Winnipeg',
  SASKATCHEWAN: 'America/Regina',
  QUEBEC: 'America/Toronto',
  'NOVA SCOTIA': 'America/Halifax',
  'NEW BRUNSWICK': 'America/Halifax',
};

// Stores are in Canada / the US, so phone numbers are validated as NANP
// (+1, area code and exchange can't start with 0 or 1).
const PHONE_ERROR_MESSAGE = 'Please enter a valid 10-digit phone number, e.g. (416) 555-0123.';
const NANP_PATTERN = /^[2-9]\d{2}[2-9]\d{6}$/;

function phoneDigits(value) {
  let digits = String(value || '').replace(/\D/g, '');
  // NANP area codes never start with 1, so a leading 1 is the country code —
  // "+1 416…" and "1-416…" (incl. browser autofill) resolve to "416…".
  if (digits.length > 1 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function formatPhone(digits) {
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidPhone(value) {
  return NANP_PATTERN.test(phoneDigits(value));
}

// E.164 — the format Shopify expects when Sesami writes the customer record.
function toE164(value) {
  return `+1${phoneDigits(value)}`;
}

// Stricter than the native type="email" check, which accepts "name@domain"
// with no TLD. Requires something@something.tld with no spaces.
const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address, e.g. name@example.com.';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[a-z]{2,}$/i;

function isValidEmail(value) {
  return EMAIL_PATTERN.test(String(value || '').trim());
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

class BookAppointmentModal extends HTMLElement {
  _state = {
    step: 'location',
    location: null,
    service: null,
    slot: null,
    slotLabel: '',
    dateLabel: '',
    timezone: null,
    wishlist: null,
    relatedProduct: null,
    preselect: {},
    sesamiLocations: [],
  };

  _sesami = null;
  _sesamiKey = null;
  _sesamiConfig = null;
  // Bumped whenever the calendar or the selected day changes, so async Sesami
  // responses that land after a service / timezone / day change are dropped.
  _calendarRequest = 0;
  _slotRequest = 0;
  _boundKeydown = null;
  _lockOwned = false;
  _locationsLoaded = false;
  _serviceConfigs = new Map();
  _serviceLocationIds = new Map();
  _triggersBound = false;
  _triggerEl = null;
  _wishlistRequest = null;

  connectedCallback() {
    this.dialog = this.querySelector('.ba-modal__dialog');
    this.form = this.querySelector('[data-ba-form]');
    this.confirmBtn = this.querySelector('[data-ba-confirm]');
    this.calendarDays = this.querySelector('[data-ba-calendar-days]');
    this.calendarLabels = this.querySelector('[data-ba-calendar-labels]');
    this.calendarLabel = this.querySelector('[data-ba-calendar-label]');
    this.calendarStatus = this.querySelector('[data-ba-calendar-status]');
    this.timesEl = this.querySelector('[data-ba-times]');
    this.selectedDateEl = this.querySelector('[data-ba-selected-date]');
    this.slotsEl = this.querySelector('[data-ba-slots]');
    this.timezoneRoot = this.querySelector('[data-ba-timezone]');
    this.timezoneTrigger = this.querySelector('[data-ba-timezone-trigger]');
    this.timezoneLabelEl = this.querySelector('[data-ba-timezone-label]');
    this.timezoneMenu = this.querySelector('[data-ba-timezone-menu]');
    this.locationGrid = this.querySelector('[data-ba-location-grid]');
    this._boundKeydown = (event) => this._onKeydown(event);
    this._selectedDay = null;
    this._selectedDayBtn = null;

    this._bindUi();
    this._bindTimezonePicker();
    this._bindGlobalTriggers();
    this._syncTimezonePicker();

    window.addEventListener(OPEN_EVENT, this._onOpenEvent);
    window.addEventListener(CLOSE_EVENT, this._onCloseEvent);
  }

  disconnectedCallback() {
    window.removeEventListener(OPEN_EVENT, this._onOpenEvent);
    window.removeEventListener(CLOSE_EVENT, this._onCloseEvent);
    document.removeEventListener('keydown', this._boundKeydown);
    this._unlockScroll();
  }

  _onOpenEvent = (event) => {
    this.open(event.detail || {});
  };

  _onCloseEvent = () => {
    this.close();
  };

  _bindGlobalTriggers() {
    // Avoid stacking duplicate listeners if the node reconnects.
    if (this._triggersBound) return;
    this._triggersBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-book-appointment-trigger]');
        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();

        const modal = document.getElementById('book-appointment-modal');
        if (!modal || typeof modal.open !== 'function') return;

        modal.open(modal._detailFromTrigger(trigger), { trigger });
      },
      true
    );
  }

  _detailFromTrigger(trigger) {
    return {
      sesamiLocationId: trigger.dataset.sesamiLocationId || null,
      // data-location-handle kept as a soft alias of data-location.
      location: trigger.dataset.location || trigger.dataset.locationHandle || null,
      productId: trigger.dataset.productId || null,
      variantId: trigger.dataset.variantId || null,
      productType: trigger.dataset.productType || null,
      // The wishlist page writes these onto its own triggers so the booking
      // carries the customer's saved products through to Sesami. The Swym
      // share link is logged-in only, so guests fall back to the item recap.
      // WISHLIST-TOGGLE: To disable, comment out the line below.
      wishlist: trigger.dataset.wishlistUrl || trigger.dataset.wishlistItems || null,
      // Written by the ring builder overlay so the design travels with the
      // booking. Product pages fall back to the current URL in _resetState().
      relatedProduct: trigger.dataset.relatedProduct || null,
    };
  }

  _bindUi() {
    this.querySelectorAll('[data-ba-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.querySelectorAll('[data-ba-step-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this._goToStep(btn.dataset.baStepBtn);
      });
    });

    this.locationGrid?.addEventListener('click', (event) => {
      const card = event.target.closest('[data-ba-location]');
      if (card) this._selectLocation(card);
    });

    this.querySelectorAll('[data-ba-service]').forEach((card) => {
      card.addEventListener('click', () => this._selectService(card));
    });

    this.querySelector('[data-ba-calendar-prev]')?.addEventListener('click', () => {
      if (!this._sesami) return;
      this._sesami.previous('month');
      this._renderCalendar();
    });

    this.querySelector('[data-ba-calendar-next]')?.addEventListener('click', () => {
      if (!this._sesami) return;
      this._sesami.next('month');
      this._renderCalendar();
    });

    this.querySelector('[data-ba-province-filters]')?.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-ba-province]');
      if (!btn) return;
      this._filterLocationsByProvince(btn.dataset.baProvince, btn);
    });

    this.phoneInput = this.form?.querySelector('[name="phone"]');
    this.phoneInput?.setAttribute('maxlength', '17');
    this.phoneInput?.setAttribute('inputmode', 'tel');
    this.phoneInput?.addEventListener('input', (event) => this._onPhoneInput(event));
    this.phoneInput?.addEventListener('blur', () => this._validatePhone({ showError: true }));

    this.emailInput = this.form?.querySelector('[name="email"]');
    // Keep an existing message in sync while typing, but only show a new one on blur.
    this.emailInput?.addEventListener('input', () =>
      this._validateEmail({ showError: this.emailInput.hasAttribute('aria-invalid') })
    );
    this.emailInput?.addEventListener('blur', () => this._validateEmail({ showError: true }));

    this.form?.addEventListener('input', () => this._syncConfirmEnabled());
    this.form?.addEventListener('change', () => this._syncConfirmEnabled());
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this._submitBooking();
    });
  }

  async open(detail = {}, options = {}) {
    try {
      const active = document.activeElement;
      this._triggerEl =
        options.trigger ||
        detail.trigger ||
        (active instanceof HTMLElement && active !== document.body && !this.contains(active)
          ? active
          : this._triggerEl);

      this._closeOpenDrawers();
      this._resetState(detail || {});
      this._resetConfirmButton();

      // Logged-in customers always carry their wishlist, whichever page the
      // booking starts from. Kicked off now so it's ready by submit.
      // WISHLIST-TOGGLE: Swym share-link fetch on open. To disable, comment out the line below.
      this._wishlistRequest = this._state.wishlist ? null : this._fetchWishlistShareUrl();

      // Show immediately so a slow Sesami request can't look like a no-op.
      this.hidden = false;
      this.setAttribute('open', '');
      this.classList.add('is-opening');
      this._lockScroll();
      document.addEventListener('keydown', this._boundKeydown);
      this._render();

      // Prevent the same click that opened the modal from hitting the overlay/close.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.classList.remove('is-opening');
          this.dialog?.focus({ preventScroll: true });
        });
      });

      await this._ensureSesamiLocations();
      this._applyPreselect();
      this._render();

      if (this._state.step === 'datetime') {
        this._initSesamiCalendar();
      }
    } catch (error) {
      console.error('[book-appointment] failed to open modal', error);
      this.hidden = false;
      this.setAttribute('open', '');
      this.classList.remove('is-opening');
    }
  }

  close() {
    const trigger = this._triggerEl;
    this._triggerEl = null;

    // Clear loader while the review button is still visible. Theme button-content
    // skips its aria-busy off animation when checkVisibility() is false.
    this._clearBookingError();
    this._resetConfirmButton();

    this.hidden = true;
    this.removeAttribute('open');
    this.classList.remove('is-opening');
    document.removeEventListener('keydown', this._boundKeydown);
    this._unlockScroll();
    this.form?.reset();
    this._validatePhone({ showError: false });
    this._validateEmail({ showError: false });
    this._resetState({});
    this._resetCalendar();
    this._render();

    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus({ preventScroll: true });
    }
  }

  _resetConfirmButton() {
    this.confirmBtn = this.querySelector('[data-ba-confirm]');
    if (!this.confirmBtn) return;

    // Theme treats aria-busy="true" as loading; prefer explicit false then remove.
    this.confirmBtn.setAttribute('aria-busy', 'false');
    this.confirmBtn.removeAttribute('aria-busy');
    // Stay disabled until the review form is valid again.
    this.confirmBtn.disabled = true;

    // Force-clear loader UI if the MutationObserver skipped the off-animation
    // (e.g. panel/modal was hidden when busy was cleared).
    const buttonContent = this.confirmBtn.querySelector('button-content');
    const shadow = buttonContent?.shadowRoot;
    if (!shadow) return;

    const transient = shadow.querySelector('.transient');
    const content = shadow.querySelector('[part="content"]');
    transient?.getAnimations?.().forEach((animation) => animation.cancel());
    content?.getAnimations?.().forEach((animation) => animation.cancel());
    if (transient) {
      transient.style.opacity = '0';
      transient.style.transform = 'translate(-50%, -50%)';
    }
    if (content) {
      content.style.opacity = '1';
      content.style.transform = 'translateY(0)';
    }
  }

  _getFocusableElements() {
    if (!this.dialog) return [];

    return [...this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)].filter((el) => {
      if (el.closest('[hidden]')) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      if (el.disabled) return false;

      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none') return false;

      return el.getClientRects().length > 0;
    });
  }

  _trapFocus(event) {
    const focusable = this._getFocusableElements();

    if (!focusable.length) {
      event.preventDefault();
      this.dialog?.focus({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const focusInside = active instanceof Node && this.dialog.contains(active);

    if (event.shiftKey) {
      if (!focusInside || active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      }
      return;
    }

    if (!focusInside || active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  _onKeydown(event) {
    if (event.key === 'Tab') {
      if (this.hidden) return;
      this._trapFocus(event);
      return;
    }

    if (event.key !== 'Escape') return;

    if (this.timezoneMenu && !this.timezoneMenu.hidden) {
      event.preventDefault();
      this._closeTimezoneMenu();
      return;
    }

    event.preventDefault();
    this.close();
  }

  _closeOpenDrawers() {
    try {
      document.querySelectorAll('[role="dialog"][open]').forEach((dialog) => {
        if (dialog === this || dialog === this.dialog || this.contains(dialog)) return;
        if (typeof dialog.hide === 'function') dialog.hide();
        else dialog.removeAttribute('open');
      });
    } catch (error) {
      console.warn('[book-appointment] failed to close drawers', error);
    }
  }

  _lockScroll() {
    if (this._lockOwned) return;
    document.documentElement.classList.add('lock');
    this._lockOwned = true;
  }

  _unlockScroll() {
    if (!this._lockOwned) return;
    document.documentElement.classList.remove('lock');
    this._lockOwned = false;
  }

  /**
   * Current URL when the booking starts on a product page, so the consultant
   * knows what the shopper was looking at. Includes the query string, which is
   * what carries the ring builder's configuration params.
   */
  _currentProductUrl() {
    if (window.themeVariables?.settings?.pageType !== 'product') return null;
    return window.location.href;
  }

  /**
   * Swym share link for a logged-in customer's wishlist, mirroring
   * wishlist-page.js: markListPublic() then generateSharedListURL(). Resolves
   * '' for guests, empty wishlists, or any Swym failure — the booking still
   * goes through without it.
   */
  _fetchWishlistShareUrl() {
    if (!this.hasAttribute('data-logged-in')) return Promise.resolve('');

    return new Promise((resolve) => {
      const run = (swat) => {
        if (typeof swat?.fetchLists !== 'function' || typeof swat.generateSharedListURL !== 'function') {
          resolve('');
          return;
        }

        swat.fetchLists({
          callbackFn: (response) => {
            const lists = Array.isArray(response)
              ? response
              : Array.isArray(response?.lists)
                ? response.lists
                : response?.lid
                  ? [response]
                  : [];
            const hasItems = (list) =>
              Number(list?.cnt || list?.count || 0) > 0 ||
              (Array.isArray(list?.listcontents) && list.listcontents.length > 0);

            // Prefer the list the wishlist page last shared, else the first non-empty one.
            const cached = this._readWishlistShareCache();
            const list =
              lists.find((item) => item.lid === cached?.lid && hasItems(item)) ||
              lists.find(hasItems);

            if (!list) {
              resolve('');
              return;
            }
            if (cached?.lid === list.lid && cached.url) {
              resolve(String(cached.url));
              return;
            }

            const generate = () => {
              swat.generateSharedListURL(
                list.lid,
                (link) => {
                  const url = typeof link === 'string' ? link : link?.url || link?.link || '';
                  if (url) this._writeWishlistShareCache(list.lid, url);
                  resolve(url);
                },
                (error) => {
                  console.warn('[book-appointment] generateSharedListURL failed', error);
                  resolve('');
                }
              );
            };

            // The link only renders for the recipient once the list is public.
            if (typeof swat.markListPublic === 'function') {
              swat.markListPublic(list.lid, generate, (error) => {
                console.warn('[book-appointment] markListPublic failed', error);
                resolve('');
              });
            } else {
              generate();
            }
          },
          errorFn: (error) => {
            console.warn('[book-appointment] fetchLists failed', error);
            resolve('');
          },
        });
      };

      // Use SwymCallbacks rather than assuming window._swat is ready.
      if (window._swat) {
        run(window._swat);
      } else {
        window.SwymCallbacks = window.SwymCallbacks || [];
        window.SwymCallbacks.push(run);
      }
    });
  }

  _readWishlistShareCache() {
    try {
      return JSON.parse(sessionStorage.getItem(SS_WISHLIST_SHARE_KEY) || 'null');
    } catch (_) {
      return null;
    }
  }

  _writeWishlistShareCache(lid, url) {
    try {
      sessionStorage.setItem(SS_WISHLIST_SHARE_KEY, JSON.stringify({ lid, url }));
    } catch (_) {
      /* ignore quota errors */
    }
  }

  /** Waits for the on-open wishlist fetch, but never blocks booking for long. */
  async _resolveWishlist() {
    if (this._state.wishlist || !this._wishlistRequest) return this._state.wishlist;

    const timeout = new Promise((resolve) => setTimeout(() => resolve(''), WISHLIST_SUBMIT_TIMEOUT_MS));
    const url = await Promise.race([this._wishlistRequest, timeout]);
    if (url) this._state.wishlist = url;
    return this._state.wishlist;
  }

  _resetState(detail) {
    this._state = {
      step: 'location',
      location: null,
      service: null,
      slot: null,
      slotLabel: '',
      dateLabel: '',
      timezone: null,
      // WISHLIST-TOGGLE: To disable, comment out the line below and uncomment `wishlist: null`.
      wishlist: detail.wishlist ? String(detail.wishlist).trim() : null,
      // wishlist: null,
      relatedProduct: detail.relatedProduct
        ? String(detail.relatedProduct).trim()
        : this._currentProductUrl(),
      preselect: {
        sesamiLocationId: detail.sesamiLocationId || null,
        location: detail.location ? String(detail.location) : null,
        productId: detail.productId ? String(detail.productId) : null,
        variantId: detail.variantId ? String(detail.variantId) : null,
        productType: detail.productType ? String(detail.productType) : null,
      },
      sesamiLocations: this._state.sesamiLocations || [],
    };

    this._selectedDay = null;
    this._selectedDayBtn = null;

    this.querySelectorAll('[data-ba-location].is-selected, [data-ba-service].is-selected').forEach((el) => {
      el.classList.remove('is-selected');
    });

    this._filterServicesByLocation(null);

    this._resetTimesPanel();
    this._syncTimezonePicker();
  }

  _resetTimesPanel() {
    if (this.selectedDateEl) this.selectedDateEl.textContent = '';
    if (this.slotsEl) this.slotsEl.innerHTML = '';
    this._selectedDay = null;
    this._selectedDayBtn = null;
    this._slotRequest += 1;
    this._closeTimezoneMenu();
  }

  /**
   * Drop the current Sesami instance and everything rendered from it. The old
   * calendar's day buttons hold closures over the old instance, so they are
   * cleared too — otherwise they stay clickable while the next one loads.
   */
  _resetCalendar() {
    this._calendarRequest += 1;
    this._sesami = null;
    this._sesamiKey = null;
    this._sesamiConfig = null;
    if (this.calendarDays) this.calendarDays.innerHTML = '';
    this._resetTimesPanel();
  }

  _timezoneOptions() {
    const options = [...TIMEZONES];
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTz || options.some((option) => option.id === browserTz)) {
      return options;
    }

    const label = this._timezoneLabel(browserTz);
    // e.g. America/New_York and America/Toronto both resolve to "Eastern Time".
    if (options.some((option) => option.label === label)) {
      return options;
    }

    options.push({ id: browserTz, label });
    return options;
  }

  /**
   * Prefer a known Canadian zone when the IANA id is an alias of the same label
   * (America/New_York → America/Toronto / Eastern Time).
   */
  _canonicalizeTimezone(timezoneId) {
    if (!timezoneId) return timezoneId;
    if (TIMEZONES.some((option) => option.id === timezoneId)) return timezoneId;

    const label = this._timezoneLabel(timezoneId);
    const match = TIMEZONES.find((option) => option.label === label);
    return match?.id || timezoneId;
  }

  _timezoneLabel(timezoneId) {
    const known = TIMEZONES.find((option) => option.id === timezoneId);
    if (known) return known.label;

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezoneId,
        timeZoneName: 'longGeneric',
      }).formatToParts(new Date());
      return parts.find((part) => part.type === 'timeZoneName')?.value || timezoneId;
    } catch {
      return timezoneId;
    }
  }

  _resolveTimezone() {
    return this._canonicalizeTimezone(
      this._state.timezone ||
        this._state.location?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'America/Toronto'
    );
  }

  _bindTimezonePicker() {
    if (!this.timezoneRoot) return;

    this.timezoneTrigger?.addEventListener('click', (event) => {
      event.stopPropagation();
      const isOpen = !this.timezoneMenu?.hidden;
      if (isOpen) this._closeTimezoneMenu();
      else this._openTimezoneMenu();
    });

    this.timezoneMenu?.addEventListener('click', (event) => {
      const option = event.target.closest('[data-ba-timezone-option]');
      if (!option) return;
      this._setTimezone(option.dataset.baTimezoneOption);
      this._closeTimezoneMenu();
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!this.timezoneRoot || this.timezoneMenu?.hidden) return;
        if (this.timezoneRoot.contains(event.target)) return;
        this._closeTimezoneMenu();
      },
      true
    );
  }

  _openTimezoneMenu() {
    if (!this.timezoneMenu || !this.timezoneTrigger) return;
    this._renderTimezoneMenu();
    this.timezoneMenu.hidden = false;
    this.timezoneTrigger.setAttribute('aria-expanded', 'true');
  }

  _closeTimezoneMenu() {
    if (!this.timezoneMenu || !this.timezoneTrigger) return;
    this.timezoneMenu.hidden = true;
    this.timezoneTrigger.setAttribute('aria-expanded', 'false');
  }

  _renderTimezoneMenu() {
    if (!this.timezoneMenu) return;

    const current = this._resolveTimezone();
    this.timezoneMenu.innerHTML = '';

    this._timezoneOptions().forEach((option) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', option.id === current ? 'true' : 'false');

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ba-modal__timezone-option';
      if (option.id === current) btn.classList.add('is-selected');
      btn.dataset.baTimezoneOption = option.id;
      btn.textContent = option.label;

      li.appendChild(btn);
      this.timezoneMenu.appendChild(li);
    });
  }

  _syncTimezonePicker() {
    if (this.timezoneLabelEl) {
      this.timezoneLabelEl.textContent = this._timezoneLabel(this._resolveTimezone());
    }
    if (!this.timezoneMenu?.hidden) this._renderTimezoneMenu();
  }

  async _setTimezone(timezoneId) {
    if (!timezoneId || timezoneId === this._resolveTimezone()) {
      this._syncTimezonePicker();
      return;
    }

    this._state.timezone = timezoneId;
    this._state.slot = null;
    this._state.slotLabel = '';
    this._syncTimezonePicker();

    if (this._sesami) {
      this._sesami.timezone = timezoneId;
    }

    // Re-fetch slots for the currently selected day in the new timezone.
    if (this._selectedDay && this._selectedDayBtn) {
      await this._selectDay(this._selectedDay, this._selectedDayBtn);
    }
  }

  async _createSesami({ productId, variantId, autoLoad = false } = {}) {
    await this._waitForSesami();

    const shopId = String(this.dataset.shopId || '');
    if (!shopId || !productId || !variantId) {
      throw new Error('Missing shopId / productId / variantId for Sesami');
    }

    // SDK 0.0.4 takes the Sesami location id natively and sends it as
    // `location` on /availabilities, /reservation and /book. That replaces
    // every workaround we needed against 0.0.3 — do not reintroduce a
    // getAvailabilities/book wrapper here: `teamMember` is now a property
    // setter that calls autoLoadIfNeeded(), so assigning it inside a request
    // wrapper re-enters the request path.
    const sesami = new window.SesamiSDK({
      shopId,
      productId: String(productId),
      variantId: String(variantId),
      quantity: 1,
      autoLoad: false,
      timezone: this._resolveTimezone(),
      location: this._state.location?.sesamiId || null,
    });

    // init() builds data.rows synchronously and schedules a debounced autoload
    // (300ms) that reads `autoLoad` when it fires. Do not `await sesami.load()`:
    // in SDK 0.0.4 load(date) returns a thunk, so awaiting it loads nothing.
    sesami.init();

    if (autoLoad) {
      // Enabling after init() lets the pending autoload prefetch the visible
      // month, and keeps next()/previous() fetching adjacent months.
      sesami.autoLoad = true;
    }

    return sesami;
  }

  /**
   * Sesami's config endpoint is /api/v1/config/{productId} — it is PER SERVICE,
   * not shop-level. Each location's resourceGroups[] inside it describe the
   * resources assigned to that location FOR THAT SERVICE, so two services can
   * expose completely different resources for the same location.
   */
  async _fetchServiceConfig(productId, variantId) {
    const key = String(productId || '');
    if (!key || !variantId) return null;
    if (this._serviceConfigs.has(key)) return this._serviceConfigs.get(key);

    let config = null;
    try {
      const sesami = await this._createSesami({ productId, variantId, autoLoad: false });

      // SDK 0.0.4's getConfig() already targets storefront.sesami.co with the
      // access token, so it is the single source for locations + resources.
      const fromSdk = await sesami.getConfig();
      if (sesami.isSesamiService !== false && fromSdk?.locations?.length) config = fromSdk;

      if (!config && sesami.configError) {
        console.warn('[book-appointment] getConfig failed', productId, sesami.configError);
      }
    } catch (error) {
      console.warn('[book-appointment] service config failed', productId, error);
    }

    this._serviceConfigs.set(key, config);
    return config;
  }

  /**
   * Re-resolve the selected location's resources against the SELECTED service.
   * Step 1 picks a location before a service exists, so the binding captured
   * then came from whichever service seeded the location list — which is the
   * wrong service's resources whenever they differ.
   */
  async _refreshLocationBindingForService() {
    const { location, service } = this._state;
    if (!location || !service?.productId) return;

    const config = await this._fetchServiceConfig(service.productId, service.variantId);
    const match = (config?.locations || []).find(
      (item) => String(item.id) === String(location.sesamiId)
    );

    if (!match) {
      console.warn(
        `[book-appointment] "${location.name}" is not offered by "${service.title}" in Sesami — ` +
          'the service config does not list this location.',
        { serviceProductId: service.productId, locationId: location.sesamiId }
      );
      return;
    }

    const resources = this._locationResources(match);
    const teamMember = this._resolveLocationTeamMember(match);

    location.resources = resources;
    location.teamMember = teamMember?.name || '';
    location.teamMemberId = teamMember?.id || '';

    if (!resources.length) {
      console.warn(
        `[book-appointment] "${location.name}" has no resources for "${service.title}". Sesami will ` +
          'auto-assign, which books the default location. Assign resources to this location for ' +
          'this service in Sesami -> Services -> Locations & Resources.'
      );
    }
  }

  async _ensureSesamiLocations() {
    if (this._locationsLoaded && this._state.sesamiLocations.length) {
      this._renderLocationCards(this._state.sesamiLocations);
      return;
    }

    const status = this.querySelector('[data-ba-location-status]');
    const services = [...this.querySelectorAll('[data-ba-service]')]
      .map((card) => ({
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
      }))
      .filter((service) => service.productId && service.variantId);

    if (!services.length) {
      if (status) {
        status.textContent = 'Add Sesami service products to the Services collection to load locations.';
      }
      return;
    }

    if (status) status.textContent = 'Loading locations…';

    try {
      // Locations are shop-level in Sesami — one successful service config is enough.
      // Prefer a preselected service, then the first collection product; stop on first hit.
      const preferredId = this._state.preselect?.productId
        ? String(this._state.preselect.productId)
        : null;
      const ordered = preferredId
        ? [
            ...services.filter((service) => String(service.productId) === preferredId),
            ...services.filter((service) => String(service.productId) !== preferredId),
          ]
        : services;

      // Every service is fetched: Sesami scopes locations per service (one
      // service may offer 8 locations, another only 2), so a single config
      // cannot describe the whole picker. Cards show the union; which services
      // are bookable at a location is filtered in _filterServicesByLocation.
      const configs = await Promise.all(
        ordered.map(async (service) => ({
          service,
          config: await this._fetchServiceConfig(service.productId, service.variantId),
        }))
      );

      const byId = new Map();
      configs.forEach(({ service, config }) => {
        const offered = config?.locations || [];
        this._serviceLocationIds.set(
          String(service.productId),
          new Set(offered.map((location) => String(location.id)))
        );
        offered.forEach((location) => {
          // Prefer whichever copy exposes resources — cards are display-only,
          // but this keeps the provisional binding as useful as possible.
          const existing = byId.get(String(location.id));
          if (!existing || (!this._locationResources(existing).length && this._locationResources(location).length)) {
            byId.set(String(location.id), location);
          }
        });
      });

      const locations = this._sortLocations([...byId.values()]);

      this._state.sesamiLocations = locations;
      this._locationsLoaded = true;
      this._renderLocationCards(locations);

      if (!locations.length && status) {
        status.textContent = 'No Sesami locations returned for this service.';
      }
    } catch (error) {
      console.error('[book-appointment] failed to load Sesami locations', error);
      if (status) {
        status.textContent = 'Unable to load Sesami locations. Confirm services are Sesami products.';
      }
    }
  }

  _normalizeProvince(value) {
    const raw = String(value || '').trim().toUpperCase();
    if (!raw) return '';

    const map = {
      AB: 'ALBERTA',
      ALBERTA: 'ALBERTA',
      BC: 'BC',
      'BRITISH COLUMBIA': 'BC',
      ON: 'ONTARIO',
      ONTARIO: 'ONTARIO',
      MB: 'MANITOBA',
      MANITOBA: 'MANITOBA',
      SK: 'SASKATCHEWAN',
      SASKATCHEWAN: 'SASKATCHEWAN',
      QC: 'QUEBEC',
      QUEBEC: 'QUEBEC',
      NS: 'NOVA SCOTIA',
      'NOVA SCOTIA': 'NOVA SCOTIA',
      NB: 'NEW BRUNSWICK',
      'NEW BRUNSWICK': 'NEW BRUNSWICK',
    };

    return map[raw] || raw;
  }

  _provinceFromText(...parts) {
    const text = parts.filter(Boolean).join(' | ');
    if (!text) return '';

    const abbr = text.match(/\b(BC|AB|ON|MB|SK|QC|NS|NB)\b/i);
    if (abbr) return this._normalizeProvince(abbr[1]);

    const full = text.match(
      /\b(Alberta|British Columbia|Ontario|Manitoba|Saskatchewan|Quebec|Nova Scotia|New Brunswick)\b/i
    );
    if (full) return this._normalizeProvince(full[1]);

    return '';
  }

  _resolveLocationProvince(location, name, address) {
    if (location?.businessAddress?.state) {
      return this._normalizeProvince(location.businessAddress.state);
    }
    return this._provinceFromText(
      name,
      address,
      location?.name,
      location?.description,
      location?.businessAddress?.city,
      location?.businessAddress?.state
    );
  }

  _businessAddressLines(businessAddress) {
    if (!businessAddress || typeof businessAddress !== 'object') return [];

    const lines = [];
    const street = String(businessAddress.address || '').trim();
    const street2 = String(businessAddress.address2 || '').trim();
    const city = String(businessAddress.city || '').trim();
    const state = String(businessAddress.state || '').trim();
    const zip = String(businessAddress.zipCode || '').trim();

    if (street) lines.push(street);
    if (street2) lines.push(street2);

    const locality = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    if (locality) lines.push(locality);

    return lines;
  }

  _locationAddressLines(location) {
    // Prefer Sesami description — same copy shown under location cards.
    if (typeof location?.description === 'string' && location.description.trim()) {
      return location.description
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    }

    return this._businessAddressLines(location?.businessAddress);
  }

  _locationPhone(location) {
    const raw = String(
      location?.phone ||
        location?.phoneNumber ||
        location?.businessAddress?.phone ||
        location?.businessAddress?.phoneNumber ||
        ''
    ).trim();
    if (!raw) return '';

    // Normalize NANP numbers to the same (416) 555-0123 format as the form;
    // anything else is shown as Sesami returns it.
    return isValidPhone(raw) ? formatPhone(phoneDigits(raw)) : raw;
  }

  _toResourceRef(value) {
    if (value == null) return null;

    if (typeof value === 'object') {
      const id = String(value.id || value.resourceId || value.teamMemberId || '');
      const name = String(value.name || value.fullName || value.title || value.displayName || '');
      return id || name ? { id, name } : null;
    }

    const raw = String(value);
    if (!raw) return null;
    // A bare string is a name unless it looks like an id.
    return /^[0-9a-f]{24}$/i.test(raw) || /^\d+$/.test(raw) || raw.startsWith('gid://')
      ? { id: raw, name: '' }
      : { id: '', name: raw };
  }

  /**
   * Every resource attached to a location.
   *
   * Sesami nests these under resourceGroups[].resources[] — NOT a flat
   * location.resources array. Flat keys are kept only as a fallback for other
   * account shapes.
   */
  _locationResources(location) {
    const groups = Array.isArray(location?.resourceGroups) ? location.resourceGroups : [];
    const nested = groups.flatMap((group) => (Array.isArray(group?.resources) ? group.resources : []));

    const flat = location?.teamMembers || location?.resources || location?.staff;
    const source = nested.length ? nested : Array.isArray(flat) ? flat : [];

    const refs = source.map((item) => this._toResourceRef(item)).filter(Boolean);
    return [...new Map(refs.map((ref) => [`${ref.id}|${ref.name}`, ref])).values()];
  }

  /**
   * Resolve the team member (resource) to book this location against.
   *
   * Routing no longer depends on this — the SDK's `location` option books the
   * right location on its own. It is resolved so the modal can warn when a
   * service/location pair has no resources configured in Sesami.
   *
   * With several resources at one location there is no single one to send, so
   * this returns null rather than guessing. Location routing does not depend on
   * it — the SDK's `location` option handles that.
   */
  _resolveLocationTeamMember(location) {
    const direct =
      this._toResourceRef(location?.teamMember) ||
      this._toResourceRef(location?.resource) ||
      this._toResourceRef(location?.teamMemberName) ||
      this._toResourceRef(location?.teamMemberId) ||
      this._toResourceRef(location?.resourceId);
    if (direct) return direct;

    const resources = this._locationResources(location);
    return resources.length === 1 ? resources[0] : null;
  }

  /**
   * Product ids bookable at this location, when Sesami scopes services per
   * location. Empty array means the config has no per-location scoping and
   * every service stays available.
   */
  _resolveLocationServiceIds(location) {
    const lists = [location?.services, location?.products, location?.serviceIds, location?.productIds];

    for (const list of lists) {
      if (!Array.isArray(list) || !list.length) continue;

      const ids = list
        .map((item) => (item && typeof item === 'object' ? item.productId || item.id : item))
        .map((id) => String(id || '').replace(/^gid:\/\/shopify\/Product\//, ''))
        .filter(Boolean);

      if (ids.length) return [...new Set(ids)];
    }

    return [];
  }

  /**
   * Loud, actionable console warning when locations cannot be bound to a Sesami
   * resource — without a binding sesami.book() falls back to the service
   * default and the customer's pick is ignored.
   */
  _warnUnboundLocations(locations) {
    const unbound = locations.filter((location) => !this._locationResources(location).length);
    if (!unbound.length) return;

    console.warn(
      `[book-appointment] ${unbound.length}/${locations.length} Sesami locations expose no resources ` +
        'for this service. Booking still routes by location id, but resource-level assignment is ' +
        'unavailable until resources are attached in Sesami -> Services -> Locations & Resources.',
      unbound.map((location) => ({ id: location.id, name: location.name }))
    );
  }

  _timezoneForProvince(province) {
    return PROVINCE_TIMEZONES[String(province || '').trim().toUpperCase()] || '';
  }

  _renderLocationCards(locations) {
    if (!this.locationGrid) return;

    this.locationGrid.innerHTML = '';

    if (!locations.length) {
      this.locationGrid.innerHTML =
        '<p class="ba-modal__empty">No Sesami locations returned for this service.</p>';
      this._rebuildProvinceFilters();
      return;
    }

    locations.forEach((location) => {
      const sesamiId = String(location.id || '');
      const name = location.name || 'Location';
      const addressLines = this._locationAddressLines(location);
      const address = addressLines.join('\n');
      const phone = this._locationPhone(location);
      const province = this._resolveLocationProvince(location, name, address);
      const image = typeof location.image === 'string' ? location.image : location.image?.url || '';
      const timezone = location.timezone || this._timezoneForProvince(province) || '';
      const handle = this._slugify(name) || '';
      const teamMember = this._resolveLocationTeamMember(location);
      const resources = this._locationResources(location);
      const serviceIds = this._resolveLocationServiceIds(location);

      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'ba-modal__location-card';
      card.dataset.baLocation = '';
      card.dataset.sesamiId = sesamiId;
      card.dataset.name = name;
      // Store as JSON so multi-line description survives data-* attribute normalization.
      card.dataset.addressLines = JSON.stringify(addressLines);
      card.dataset.province = province;
      card.dataset.timezone = timezone;
      card.dataset.handle = handle;
      card.dataset.image = image;
      // Informational only — see _resolveLocationTeamMember.
      card.dataset.teamMember = teamMember?.name || '';
      card.dataset.teamMemberId = teamMember?.id || '';
      card.dataset.resources = JSON.stringify(resources);
      card.dataset.serviceIds = JSON.stringify(serviceIds);

      card.innerHTML = `
        <div class="ba-modal__location-media">
          ${
            image
              ? `<img src="${image}" alt="" width="600" height="375" loading="lazy">`
              : '<div class="ba-modal__media-placeholder" aria-hidden="true"></div>'
          }
        </div>
        <div class="ba-modal__location-info">
          <p class="ba-modal__location-name ba-modal__title"></p>
          ${addressLines.length ? '<p class="ba-modal__location-address"></p>' : ''}
          ${phone ? '<p class="ba-modal__location-phone"></p>' : ''}
        </div>
      `;

      card.querySelector('.ba-modal__location-name').textContent = name;
      const addressEl = card.querySelector('.ba-modal__location-address');
      if (addressEl) {
        addressLines.forEach((line, index) => {
          if (index > 0) addressEl.appendChild(document.createElement('br'));
          addressEl.appendChild(document.createTextNode(line));
        });
      }
      const phoneEl = card.querySelector('.ba-modal__location-phone');
      if (phoneEl) phoneEl.textContent = phone;

      this.locationGrid.appendChild(card);
    });

    this._warnUnboundLocations(locations);
    this._rebuildProvinceFilters();
  }

  /**
   * Order locations by the section's comma-separated "Location order" setting.
   * Entries match a location's Sesami ID exactly, or any location whose name
   * contains the entry (names carry a province suffix, e.g. "Calgary, AB"). Unlisted
   * locations keep their default (Sesami) order after the listed ones.
   */
  _sortLocations(locations) {
    const entries = String(this.dataset.locationOrder || '')
      .split(/[,\n]/)
      .map((entry) => this._normalizeMatchKey(entry))
      .filter(Boolean);
    if (!entries.length) return locations;

    const rankOf = (location) => {
      const id = this._normalizeMatchKey(location.id);
      const name = this._normalizeMatchKey(location.name);
      const slug = this._slugify(location.name);
      const index = entries.findIndex(
        (entry) =>
          entry === id ||
          name.includes(entry) ||
          (this._slugify(entry) !== '' && slug.includes(this._slugify(entry)))
      );
      return index === -1 ? Infinity : index;
    };

    return locations
      .map((location, index) => ({ location, index, rank: rankOf(location) }))
      .sort((a, b) => (a.rank === b.rank ? a.index - b.index : a.rank - b.rank))
      .map(({ location }) => location);
  }

  _rebuildProvinceFilters() {
    const tabs = this.querySelector('[data-ba-province-filters]');
    if (!tabs) return;

    tabs.querySelectorAll('[data-ba-province]:not([data-ba-province="all"])').forEach((el) => el.remove());

    const provinces = [
      ...new Set(
        [...this.querySelectorAll('[data-ba-location]')]
          .map((el) => (el.dataset.province || '').trim().toUpperCase())
          .filter(Boolean)
      ),
    ].sort();

    // Preferred Spence order when present.
    const preferred = ['ALBERTA', 'BC', 'ONTARIO'];
    const ordered = [
      ...preferred.filter((p) => provinces.includes(p)),
      ...provinces.filter((p) => !preferred.includes(p)),
    ];

    ordered.forEach((province) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ba-modal__tab link-reversed subheading';
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');
      btn.dataset.baProvince = province;
      btn.textContent = province;
      tabs.appendChild(btn);
    });

    tabs.hidden = ordered.length === 0;

    const allBtn = tabs.querySelector('[data-ba-province="all"]');
    this._filterLocationsByProvince('all', allBtn);
  }

  _filterLocationsByProvince(value, activeBtn) {
    const tabs = this.querySelector('[data-ba-province-filters]');
    tabs?.querySelectorAll('[data-ba-province]').forEach((el) => {
      const isActive = el === activeBtn;
      el.classList.remove('link', 'link-reversed', 'is-active');
      el.classList.add(isActive ? 'link' : 'link-reversed');
      if (isActive) el.classList.add('is-active');
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.querySelectorAll('[data-ba-location]').forEach((card) => {
      const match = value === 'all' || (card.dataset.province || '').toUpperCase() === value;
      card.hidden = !match;
    });
  }

  _normalizeMatchKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  _slugify(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Match a Sesami service card to a storefront product type.
   * Prefer exact product.type, then exact service title
   * (e.g. PDP type "Engagement Rings" → service "Engagement Rings").
   */
  _findServiceByType(productType) {
    const needle = this._normalizeMatchKey(productType);
    if (!needle) return null;

    const cards = [...this.querySelectorAll('[data-ba-service]')];

    const exact =
      cards.find((card) => this._normalizeMatchKey(card.dataset.productType) === needle) ||
      cards.find((card) => this._normalizeMatchKey(card.dataset.title) === needle);

    if (exact) return exact;

    // Singular / plural is not worth a miss: "Engagement Ring" and "Engagement Rings"
    // name the same service, and the two are spelled either way across the catalog.
    const singularize = (value) => value.replace(/s$/, '');
    const loose = singularize(needle);
    if (!loose) return null;

    return (
      cards.find((card) => singularize(this._normalizeMatchKey(card.dataset.productType)) === loose) ||
      cards.find((card) => singularize(this._normalizeMatchKey(card.dataset.title)) === loose) ||
      null
    );
  }

  /**
   * Resolve a CTA hint (name / slug / id) to a Sesami location id.
   */
  _resolveSesamiIdFromHint(hint) {
    if (!hint) return null;

    const raw = String(hint).trim();
    const normalized = this._normalizeMatchKey(raw);
    const slug = this._slugify(raw);

    const fromSesami = (this._state.sesamiLocations || []).find((location) => {
      return (
        String(location?.id || '') === raw ||
        this._normalizeMatchKey(location?.name) === normalized ||
        this._slugify(location?.name) === slug
      );
    });

    return fromSesami?.id ? String(fromSesami.id) : null;
  }

  /**
   * Resolve a CTA location hint to a rendered location card.
   * Accepts Sesami id, location name, or slug (e.g. vancouver-bc).
   */
  _findLocationCard({ sesamiLocationId, location } = {}) {
    const cards = [...this.querySelectorAll('[data-ba-location]')];
    if (!cards.length) return null;

    const bySesamiId = (id) => {
      if (!id) return null;
      return cards.find((card) => String(card.dataset.sesamiId || '') === String(id)) || null;
    };

    const byKey = (value) => {
      const needle = this._normalizeMatchKey(value);
      const slug = this._slugify(value);
      if (!needle && !slug) return null;

      return (
        cards.find((card) => this._normalizeMatchKey(card.dataset.sesamiId) === needle) ||
        cards.find((card) => this._normalizeMatchKey(card.dataset.handle) === needle) ||
        cards.find((card) => this._slugify(card.dataset.handle) === slug) ||
        cards.find((card) => this._normalizeMatchKey(card.dataset.name) === needle) ||
        cards.find((card) => this._slugify(card.dataset.name) === slug) ||
        null
      );
    };

    let card = bySesamiId(sesamiLocationId);
    if (card) return card;

    if (location) {
      const resolvedId = this._resolveSesamiIdFromHint(location);
      card = bySesamiId(resolvedId) || byKey(location);
      if (card) return card;
    }

    return null;
  }

  _applyPreselect() {
    const { sesamiLocationId, location, productId, productType } = this._state.preselect;

    const locationCard = this._findLocationCard({
      sesamiLocationId,
      location,
    });

    if (locationCard) {
      // Keep filtered-away cards visible when preselecting.
      locationCard.hidden = false;
      this._selectLocation(locationCard, { advance: false });
    } else if (sesamiLocationId || location) {
      console.warn('[book-appointment] could not preselect location', {
        sesamiLocationId,
        location,
        sesamiLocationNames: (this._state.sesamiLocations || []).map((item) => ({
          id: item.id,
          name: item.name,
          slug: this._slugify(item.name),
        })),
      });
    }

    let serviceCard = null;
    if (productId) {
      serviceCard = this.querySelector(`[data-ba-service][data-product-id="${CSS.escape(productId)}"]`);
    }
    if (!serviceCard && productType) {
      serviceCard = this._findServiceByType(productType);
    }

    if (serviceCard) {
      serviceCard.hidden = false;
      this._selectService(serviceCard, { advance: false });
    }

    // A preselected location completes that step — open on Service, same as a manual pick. Remove to always start on location.
    if (this._state.location) {
      this._goToStep('service');
      return;
    }

    this._state.step = 'location';
    this._render();
  }

  _cardAddressLines(card, sesamiLoc) {
    try {
      const stored = JSON.parse(card?.dataset?.addressLines || '[]');
      if (Array.isArray(stored) && stored.length) {
        return stored.map((line) => String(line || '').trim()).filter(Boolean);
      }
    } catch (_) {
      /* ignore invalid JSON */
    }

    return this._locationAddressLines(sesamiLoc);
  }

  _selectLocation(card, { advance = true } = {}) {
    this.querySelectorAll('[data-ba-location]').forEach((el) => el.classList.remove('is-selected'));
    card.classList.add('is-selected');

    const sesamiId = card.dataset.sesamiId || '';
    const sesamiLoc = this._state.sesamiLocations.find((location) => String(location.id) === String(sesamiId));
    const addressLines = this._cardAddressLines(card, sesamiLoc);

    this._state.location = {
      sesamiId,
      handle: card.dataset.handle || '',
      name: card.dataset.name,
      address: addressLines.join('\n'),
      addressLines,
      image: card.dataset.image || '',
      timezone: card.dataset.timezone || '',
      province: card.dataset.province || '',
      // Informational only — routing uses the SDK's `location` option.
      teamMember: card.dataset.teamMember || '',
      teamMemberId: card.dataset.teamMemberId || '',
      resources: this._parseJsonList(card.dataset.resources, { raw: true }),
      serviceIds: this._parseJsonList(card.dataset.serviceIds),
    };

    this._filterServicesByLocation(this._state.location);

    // Default picker to the location timezone when available.
    this._state.timezone = card.dataset.timezone || this._state.timezone || null;
    this._syncTimezonePicker();

    this._state.slot = null;
    this._state.slotLabel = '';
    this._state.dateLabel = '';
    this._resetCalendar();

    if (advance) this._goToStep('service');
    else this._render();
  }

  _parseJsonList(value, { raw = false } = {}) {
    try {
      const parsed = JSON.parse(value || '[]');
      if (!Array.isArray(parsed)) return [];
      return raw ? parsed : parsed.map((item) => String(item));
    } catch (_) {
      return [];
    }
  }

  /**
   * Hide services the selected location cannot book. Only applies when Sesami
   * scopes services per location; with no scoping every service stays visible.
   */
  _filterServicesByLocation(location) {
    const cards = [...this.querySelectorAll('[data-ba-service]')];
    if (!cards.length) return;

    const locationId = String(location?.sesamiId || '');
    const allowed = location?.serviceIds || [];

    cards.forEach((card) => {
      const productId = String(card.dataset.productId || '');

      // Sesami's own per-service location list is authoritative when we have it.
      const offered = locationId ? this._serviceLocationIds.get(productId) : null;
      if (offered) {
        card.hidden = !offered.has(locationId);
        return;
      }

      card.hidden = allowed.length ? !allowed.includes(productId) : false;
    });

    const status = this.querySelector('[data-ba-service-status]');
    if (!status) return;

    const hasVisible = cards.some((card) => !card.hidden);
    status.textContent = hasVisible ? '' : 'No services are available at this location.';
    status.hidden = hasVisible;
  }

  _selectService(card, { advance = true } = {}) {
    this.querySelectorAll('[data-ba-service]').forEach((el) => el.classList.remove('is-selected'));
    card.classList.add('is-selected');

    this._state.service = {
      productId: card.dataset.productId,
      variantId: card.dataset.variantId,
      title: card.dataset.title,
      duration: card.dataset.duration || '',
    };

    this._state.slot = null;
    this._state.slotLabel = '';
    this._state.dateLabel = '';
    this._resetCalendar();

    if (advance) this._goToStep('datetime');
    else this._render();
  }

  _goToStep(step) {
    if (!STEPS.includes(step)) return;

    const order = { location: 0, service: 1, datetime: 2, review: 3, confirmation: 4 };
    const target = order[step];

    if (target >= 1 && !this._state.location) return;
    if (target >= 2 && !this._state.service) return;
    if (target >= 3 && !this._state.slot) return;

    this._state.step = step;
    this._render();

    if (step === 'datetime') this._initSesamiCalendar();
    if (step === 'review') this._updateRecap();
  }

  _render() {
    const { step, location, service, dateLabel, slotLabel } = this._state;

    this.querySelectorAll('[data-ba-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.baPanel !== step;
    });

    this.querySelector('[data-ba-main]')?.classList.toggle('is-confirmation', step === 'confirmation');

    this.querySelectorAll('[data-ba-step-btn]').forEach((btn) => {
      const key = btn.dataset.baStepBtn;
      const completed =
        (key === 'location' && location) ||
        (key === 'service' && service) ||
        (key === 'datetime' && this._state.slot) ||
        (key === 'review' && step === 'review');

      const canVisit =
        key === 'location' ||
        (key === 'service' && location) ||
        (key === 'datetime' && location && service) ||
        (key === 'review' && location && service && this._state.slot);

      btn.disabled = !canVisit;
      btn.classList.toggle('is-active', key === step);
      btn.classList.toggle('is-complete', Boolean(completed) && key !== step);
      btn.setAttribute('aria-current', key === step ? 'step' : 'false');
    });

    this._setStepValue('location', location?.name || '');
    this._setStepValue('service', service?.title || '');
    this._setStepValue('datetime', dateLabel && slotLabel ? `${dateLabel}, ${slotLabel}` : '');
  }

  _setStepValue(key, value) {
    const el = this.querySelector(`[data-ba-step-value="${key}"]`);
    if (el) el.textContent = value;
  }

  async _initSesamiCalendar() {
    if (!this._state.service || !this._state.location) return;

    const requestId = ++this._calendarRequest;
    const isStale = () => requestId !== this._calendarRequest;

    // Resources are per service — rebind before availability or booking runs.
    await this._refreshLocationBindingForService();
    if (isStale()) return;

    const shopId = String(this.dataset.shopId || '');
    const productId = String(this._state.service.productId || '');
    const variantId = String(this._state.service.variantId || '');
    const locationId = String(this._state.location.sesamiId || '');
    const nextKey = `${shopId}:${productId}:${variantId}:${locationId}:${this._resolveTimezone()}`;

    if (!shopId || !productId || !variantId) {
      this._setCalendarStatus('Missing shop / service IDs for Sesami.');
      return;
    }

    if (this._sesami && this._sesamiKey === nextKey) {
      this._renderCalendar();
      return;
    }

    this._setCalendarStatus('Loading calendar…');

    try {
      const sesami = await this._createSesami({
        productId,
        variantId,
        autoLoad: true,
      });
      if (isStale()) return;

      this._sesami = sesami;
      this._sesamiKey = nextKey;
      window.sesami = sesami;

      this._renderCalendar();

      const config = await sesami.getConfig();
      if (isStale()) return;

      this._sesamiConfig = config;
      if (sesami.isSesamiService === false) {
        this._setCalendarStatus('This product is not configured as a Sesami service.');
      } else if (!this._sesamiConfig?.instantBooking?.enabled) {
        console.warn(
          '[book-appointment] Instant Booking is OFF for this service. sesami.book() will fail until it is enabled in Sesami → Services.'
        );
      }
    } catch (error) {
      if (isStale()) return;
      console.error('[book-appointment] Sesami init failed', error);
      this._sesami = null;
      this._sesamiKey = null;
      this._sesamiConfig = null;
      this._setCalendarStatus('Unable to load Sesami. Check the SDK script and service product IDs.');
    }
  }

  _waitForSesami(timeoutMs = 10000) {
    if (window.SesamiSDK) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const timer = setInterval(() => {
        if (window.SesamiSDK) {
          clearInterval(timer);
          resolve();
          return;
        }

        if (Date.now() - startedAt > timeoutMs) {
          clearInterval(timer);
          reject(new Error('SesamiSDK not available on window'));
        }
      }, 50);
    });
  }

  _setCalendarStatus(message) {
    if (!this.calendarStatus) return;

    if (!message) {
      this.calendarStatus.hidden = true;
      this.calendarStatus.textContent = '';
      return;
    }

    this.calendarStatus.hidden = false;
    this.calendarStatus.textContent = message;
  }

  _renderCalendar() {
    if (!this._sesami?.data?.rows?.length) {
      this._setCalendarStatus('Calendar data unavailable.');
      this._resetTimesPanel();
      return;
    }

    const { labels = [], rows = [] } = this._sesami.data;
    const dayName = (date) =>
      new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(new Date(date)).toUpperCase();

    this._resetTimesPanel();

    if (this.calendarLabels) {
      this.calendarLabels.innerHTML = '';
      labels.forEach((date) => {
        const li = document.createElement('li');
        li.textContent = dayName(date);
        this.calendarLabels.appendChild(li);
      });
    }

    if (this.calendarLabel) {
      this.calendarLabel.textContent = this._sesami.formatDate(this._sesami.now, {
        month: 'long',
        year: 'numeric',
      });
    }

    // Auto-select the first selectable day: today in the current month, or the
    // first non-past day after navigating to another month.
    let firstDay = null;
    let firstBtn = null;

    if (this.calendarDays) {
      this.calendarDays.innerHTML = '';

      rows.forEach((row) => {
        row.forEach((day) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ba-modal__day';
          btn.textContent = new Date(day.date).getDate();

          if (!day.isCurrentMonth) btn.classList.add('is-outside');
          if (day.isToday) btn.classList.add('is-today');

          if (day.isPast || !day.isCurrentMonth) {
            btn.disabled = true;
            btn.classList.add('is-past');
          } else {
            btn.addEventListener('click', () => this._selectDay(day, btn));
            if (!firstDay) {
              firstDay = day;
              firstBtn = btn;
            }
          }

          li.appendChild(btn);
          this.calendarDays.appendChild(li);
        });
      });
    }

    this._syncTimezonePicker();
    this._setCalendarStatus('');

    if (firstDay && firstBtn) {
      this._selectDay(firstDay, firstBtn);
    }
  }

  /**
   * Format a Sesami calendar day as a stable calendar date label.
   * Avoid formatting with the picker timezone — date-only values at UTC midnight
   * otherwise shift to the previous day in US/Canada zones.
   */
  _formatCalendarDayLabel(day) {
    const raw = String(day?.date ?? '');
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const date = match
      ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12))
      : new Date(raw);

    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  _setSlotsStatus(message) {
    if (!this.slotsEl) return;

    this.slotsEl.innerHTML = '';
    if (!message) return;

    const status = document.createElement('p');
    status.className = 'ba-modal__slots-status';
    status.textContent = message;
    this.slotsEl.appendChild(status);
  }

  async _selectDay(day, btn) {
    this.querySelectorAll('.ba-modal__day.is-selected').forEach((el) => el.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    this._selectedDay = day;
    this._selectedDayBtn = btn;

    const sesami = this._sesami;
    const requestId = ++this._slotRequest;
    // A newer day / timezone / service selection superseded this request.
    const isStale = () => requestId !== this._slotRequest || sesami !== this._sesami;

    this._state.dateLabel = this._formatCalendarDayLabel(day);
    this._state.slot = null;
    this._state.slotLabel = '';

    if (this.selectedDateEl) this.selectedDateEl.textContent = this._state.dateLabel;
    this._syncTimezonePicker();
    this._setCalendarStatus('');
    this._setSlotsStatus('Loading times…');

    try {
      if (sesami) sesami.timezone = this._resolveTimezone();
      const availabilities = await day.getAvailabilities();
      if (isStale()) return;
      this._renderSlots(Array.isArray(availabilities) ? availabilities : []);
    } catch (error) {
      if (isStale()) return;
      console.error('[book-appointment] availabilities failed', error);
      this._setSlotsStatus('Unable to load times for this day. Check timezone / location setup.');
    }
  }

  _renderSlots(slots) {
    if (!this.slotsEl) return;

    this.slotsEl.innerHTML = '';

    if (!slots.length) {
      this._setSlotsStatus('No times available for this day.');
      return;
    }

    slots.forEach((slot) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ba-modal__slot';
      btn.textContent = this._formatSlotLabel(slot);
      btn.addEventListener('click', () => {
        this.querySelectorAll('.ba-modal__slot.is-selected').forEach((el) => el.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        this._state.slot = slot;
        this._state.slotLabel = btn.textContent;
        this._goToStep('review');
      });
      this.slotsEl.appendChild(btn);
    });
  }

  _parseSlotDate(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;

    // Prefer absolute ISO timestamps when Sesami provides them.
    const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
    const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');

    if (hasOffset) return new Date(normalized);

    // Treat naive datetimes as wall-clock in the active timezone.
    const timezone = this._resolveTimezone();
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
    );
    if (!match) return new Date(normalized);

    const [, year, month, day, hour, minute, second = '0'] = match;
    const asUtc = Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date(asUtc));

      const read = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
      const asTzUtc = Date.UTC(
        read('year'),
        read('month') - 1,
        read('day'),
        read('hour'),
        read('minute'),
        read('second')
      );
      return new Date(asUtc - (asTzUtc - asUtc));
    } catch {
      return new Date(normalized);
    }
  }

  _formatSlotLabel(slot) {
    const start = this._parseSlotDate(slot.startTime);
    if (!start || Number.isNaN(start.getTime())) return String(slot.startTime || '');

    const end = new Date(start.getTime() + (slot.duration || 0) * 60000);
    const fmt = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: this._resolveTimezone(),
    });
    return `${fmt.format(start)} - ${fmt.format(end)}`;
  }

  _setMultilineText(el, linesOrText) {
    if (!el) return;

    el.replaceChildren();
    const lines = Array.isArray(linesOrText)
      ? linesOrText.map((line) => String(line || '').trim()).filter(Boolean)
      : String(linesOrText || '')
          .split(/\n/)
          .map((line) => line.trim())
          .filter(Boolean);

    lines.forEach((line, index) => {
      if (index > 0) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(line));
    });
  }

  _syncRecapInfoVisibility(root = this) {
    root.querySelectorAll('[data-ba-recap-info]').forEach((row) => {
      const span = row.querySelector('span');
      row.hidden = !span?.textContent?.trim();
    });
  }

  _updateRecap() {
    const { location, service, dateLabel, slotLabel } = this._state;
    const media = this.querySelector('[data-ba-recap-media]');
    const locEl = this.querySelector('[data-ba-recap-location]');
    const addressEl = this.querySelector('[data-ba-recap-address]');
    const serviceEl = this.querySelector('[data-ba-recap-service]');
    const dateEl = this.querySelector('[data-ba-recap-date]');
    const timeEl = this.querySelector('[data-ba-recap-time]');

    if (media) {
      media.innerHTML = location?.image
        ? `<img src="${location.image}" alt="" width="320" height="180" loading="lazy">`
        : '';
    }
    if (locEl) locEl.textContent = location?.name || '';
    this._setMultilineText(addressEl, location?.addressLines || location?.address || '');
    if (serviceEl) serviceEl.textContent = service?.title || '';
    if (dateEl) dateEl.textContent = dateLabel || '';
    if (timeEl) timeEl.textContent = slotLabel || '';

    this._syncRecapInfoVisibility();
  }

  _syncConfirmEnabled() {
    if (!this.form || !this.confirmBtn) return;
    const tos = this.form.querySelector('[name="agree_tos"]');
    // The terms checkbox only renders when enabled in the section settings.
    const valid = this.form.checkValidity() && (!tos || tos.checked);
    this.confirmBtn.disabled = !valid;
  }

  _onPhoneInput(event) {
    const input = event.target;
    const raw = input.value;
    const caret = input.selectionStart ?? raw.length;
    let digits = phoneDigits(raw);
    let digitsBeforeCaret = phoneDigits(raw.slice(0, caret)).length;

    // Backspacing over a formatting character ("(", ")", " ", "-") leaves the
    // digits unchanged and the formatter would put it straight back — delete the
    // digit before it instead so the caret isn't stuck.
    if (
      event.inputType === 'deleteContentBackward' &&
      digits === this._phoneDigits &&
      digitsBeforeCaret > 0
    ) {
      digits = digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
      digitsBeforeCaret -= 1;
    }

    const formatted = formatPhone(digits);
    this._phoneDigits = digits;

    if (formatted !== raw) {
      input.value = formatted;
      // Restore the caret after the same number of digits it followed before.
      let position = 0;
      let seen = 0;
      while (position < formatted.length && seen < digitsBeforeCaret) {
        if (/\d/.test(formatted[position])) seen += 1;
        position += 1;
      }
      if (document.activeElement === input) input.setSelectionRange(position, position);
    }

    // Only surface the inline message once the user has left the field, but
    // clear it as soon as the number becomes valid.
    this._validatePhone({ showError: this.phoneInput.hasAttribute('aria-invalid') });
  }

  _validatePhone({ showError = false } = {}) {
    const input = this.phoneInput;
    if (!input) return true;
    if (!input.value.trim()) this._phoneDigits = '';
    return this._validateField(input, isValidPhone, PHONE_ERROR_MESSAGE, showError);
  }

  _validateEmail({ showError = false } = {}) {
    const input = this.emailInput;
    if (!input) return true;
    // Also honour the native type="email" check so the inline message always
    // explains why the confirm button is disabled.
    const isValid = (value) => isValidEmail(value) && !input.validity.typeMismatch;
    return this._validateField(input, isValid, EMAIL_ERROR_MESSAGE, showError);
  }

  _validateField(input, isValid, message, showError) {
    const value = input.value.trim();
    // Empty is left to the native `required` check.
    const valid = !value || isValid(value);
    input.setCustomValidity(valid ? '' : message);

    const showMessage = showError && !valid;
    const errorEl = this._fieldErrorEl(input, showMessage);
    if (errorEl) {
      errorEl.hidden = !showMessage;
      errorEl.textContent = showMessage ? message : '';
    }
    if (showMessage) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');

    return valid;
  }

  _fieldErrorEl(input, create) {
    const id = `${input.id}-error`;
    const existing = this.querySelector(`[id="${id}"]`);
    if (existing || !create) return existing;

    const errorEl = document.createElement('p');
    errorEl.id = id;
    errorEl.className = 'ba-modal__field-error';
    errorEl.setAttribute('aria-live', 'polite');
    errorEl.hidden = true;
    (input.closest('.form-control') || input).after(errorEl);
    input.setAttribute('aria-describedby', errorEl.id);

    return errorEl;
  }

  async _readSesamiError(error) {
    if (!error) return 'Booking failed.';
    if (typeof error === 'string') return error;
    if (error instanceof Error && error.message) return error.message;

    // Sesami SDK throws the fetch Response on non-2xx.
    if (typeof error.json === 'function') {
      try {
        const data = await error.clone?.().json?.() ?? await error.json();
        return data?.message || data?.error || data?.errors?.[0] || 'Booking failed.';
      } catch (_) {
        try {
          const text = await error.clone?.().text?.() ?? await error.text();
          if (text) return text;
        } catch (_) {
          /* ignore */
        }
      }
    }

    return error.message || 'Booking failed.';
  }

  _showBookingError(message) {
    let banner = this.querySelector('[data-ba-booking-error]');
    if (!banner && this.form) {
      banner = document.createElement('p');
      banner.className = 'ba-modal__booking-error';
      banner.dataset.baBookingError = '';
      this.form.prepend(banner);
    }
    if (banner) {
      banner.hidden = false;
      banner.textContent = message;
    } else {
      alert(message);
    }
  }

  _clearBookingError() {
    const banner = this.querySelector('[data-ba-booking-error]');
    if (banner) {
      banner.hidden = true;
      banner.textContent = '';
    }
  }

  _showConfirmation(firstName) {
    // Clear review submit loading before leaving the review step.
    this._resetConfirmButton();

    const heading = this.querySelector('[data-ba-confirm-heading]');
    if (heading) {
      const template = this.dataset.confirmTitle || "You're all set!";
      heading.textContent = template.replace(/\[name\]/gi, firstName || '');
    }

    const copyEl = this.querySelector('[data-ba-confirm-copy]');
    if (copyEl) {
      copyEl.textContent = this.dataset.confirmCopy || copyEl.textContent;
    }

    const confirmRecap = this.querySelector('[data-ba-confirm-recap]');
    const sourceRecap = this.querySelector('[data-ba-recap]');
    if (confirmRecap && sourceRecap) {
      confirmRecap.innerHTML = sourceRecap.innerHTML;
      this._syncRecapInfoVisibility(confirmRecap);
    }

    this._state.step = 'confirmation';
    this._render();
  }

  async _submitBooking() {
    if (!this.form || !this._state.slot || !this._sesami) return;
    this._validateEmail({ showError: true });
    this._validatePhone({ showError: true });
    if (!this.form.reportValidity()) return;

    // Re-resolve in case connect-time query missed the button snippet markup.
    this.confirmBtn = this.querySelector('[data-ba-confirm]');

    this._clearBookingError();

    const data = new FormData(this.form);
    const customer = {
      firstName: String(data.get('firstName') || '').trim(),
      lastName: String(data.get('lastName') || '').trim(),
      email: String(data.get('email') || '').trim(),
      phone: toE164(data.get('phone')),
    };
    const preferredCommunication = String(data.get('preferred_communication') || '').trim();
    const notes = String(data.get('notes') || '').trim();

    if (this.confirmBtn) {
      this.confirmBtn.disabled = true;
      this.confirmBtn.setAttribute('aria-busy', 'true');
    }

    try {
      // Refresh config — sesami.book() only works with Instant Booking enabled.
      this._sesamiConfig = this._sesamiConfig || (await this._sesami.getConfig());

      if (!this._sesamiConfig?.instantBooking?.enabled) {
        throw new Error(
          'Instant Booking is not enabled for this Sesami service. In Shopify Admin go to Sesami → Services → open this service → enable Instant Booking (use “Just Book” for complimentary appointments), then save and try again.'
        );
      }

      this._sesami.timezone = this._resolveTimezone();

      // WISHLIST-TOGGLE: To disable, comment out the line below.
      await this._resolveWishlist();

      // Custom properties are stored on the Sesami appointment and included in
      // Sesami notification emails that surface appointment properties.
      const properties = [
        ...(this.form?.querySelector('[name="agree_tos"]')?.checked
          ? [{ name: 'agree_with_terms', value: 'yes' }]
          : []),
        // The SDK sends the location id as body.location; this is the
        // human-readable copy for the appointment record and staff emails.
        ...(this._state.location?.name
          ? [{ name: 'Location', value: this._state.location.name }]
          : []),
        ...(preferredCommunication
          ? [{ name: 'Preferred mode of communication', value: preferredCommunication }]
          : []),
        ...(notes ? [{ name: 'Note', value: notes }] : []),
        // Swym share link (or recap) from the wishlist page trigger, or the
        // logged-in customer's share link fetched on open — gives the expert
        // the customer's saved products before the appointment.
        // WISHLIST-TOGGLE: To disable, comment out the line below.
        ...(this._state.wishlist ? [{ name: 'Wishlist', value: this._state.wishlist }] : []),
        // Link back to the product the booking was started from — a ring builder
        // design or any product page — so the consultant can open it beforehand.
        ...(this._state.relatedProduct
          ? [{ name: 'Your chosen pieces', value: this._state.relatedProduct }]
          : []),
      ];

      await this._sesami.book(this._state.slot, customer, { properties });

      this._showConfirmation(customer.firstName);
    } catch (error) {
      console.error('[book-appointment] booking failed', error);
      const message = await this._readSesamiError(error);
      const isInstantBookingError = /instant booking/i.test(message);

      this._showBookingError(
        isInstantBookingError
          ? 'Instant Booking is not enabled for this service. Enable it in Sesami → Services → [service] → Instant Booking → Just Book (recommended for complimentary appointments).'
          : message || 'Sorry — we could not complete your booking. Please try again.'
      );
      this._resetConfirmButton();
      this._syncConfirmEnabled();
    } finally {
      // Always clear loader (incl. theme button-content shadow UI).
      this._resetConfirmButton();
      if (this._state.step === 'review') this._syncConfirmEnabled();
    }
  }
}

if (!customElements.get('book-appointment-modal')) {
  customElements.define('book-appointment-modal', BookAppointmentModal);
}

window.BookAppointment = {
  open(detail = {}) {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail }));
  },
  close() {
    window.dispatchEvent(new CustomEvent(CLOSE_EVENT));
  },
};
